import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PenLine, ArrowRight, BadgeCheck } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { PageLoader, useMinLoading } from "@/components/Preloader";
import { Seo } from "@/components/Seo";

export default function AuthorsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["authors-directory"],
    queryFn: async () => (await api.get("/authors")).data,
    staleTime: 5 * 60 * 1000,
  });
  const showLoader = useMinLoading(isLoading, 600, "authors");
  if (showLoader) return <PageLoader />;
  const authors = data?.authors || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="authors-directory-page">
      <Seo title="Our Authors" path="/authors"
        description="Meet the NewsLayer Approved Authors — independent voices covering crypto, DeFi, regulation and the onchain economy." />
      <header className="mb-10 border-b border-white/10 pb-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#7C3AED] flex items-center gap-2 mb-3">
          <PenLine className="h-4 w-4" /> NewsLayer Author Program
        </p>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-white text-3xl sm:text-5xl">Our Authors</h1>
            <p className="text-gray-400 text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">
              Independent voices, vetted by our editors — covering crypto, DeFi, regulation and the
              onchain economy. Every story is editorially reviewed before it's published.
            </p>
          </div>
          <Link to="/author-program" data-testid="authors-become-cta"
            className="inline-flex items-center gap-2 rounded-lg bg-[#7C3AED] text-white font-bold text-sm px-5 py-2.5 hover:bg-[#6a2dd0] transition-colors">
            Become an author <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {authors.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-xl">
          <p className="text-gray-400">Our first authors are being onboarded — check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {authors.map((a: any) => (
            <Link key={a.handle} to={`/author/${a.handle}`} data-testid={`author-card-${a.handle}`}
              className="group rounded-xl border border-white/[0.08] bg-[#0b0c11] p-5 hover:border-[#7C3AED]/50 transition-colors">
              <div className="flex items-center gap-3">
                {a.avatar_url ? (
                  <img src={a.avatar_url} alt={a.name}
                    className="h-12 w-12 rounded-full object-cover border border-white/10 shrink-0" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center shrink-0">
                    <span className="font-heading font-black text-white text-sm">
                      {a.name.split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-white font-heading font-bold flex items-center gap-1.5 truncate group-hover:text-[#00E5FF] transition-colors">
                    {a.name} <BadgeCheck className="h-3.5 w-3.5 text-[#7C3AED] shrink-0" />
                  </p>
                  <p className="font-mono text-[11px] text-[#00E5FF]">@{a.handle}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mt-3 line-clamp-2 min-h-[2.5rem]">
                {a.bio || "NewsLayer Approved Author."}
              </p>
              {(a.expertise || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {a.expertise.slice(0, 3).map((x: string) => (
                    <span key={x} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-gray-400">
                      {x}
                    </span>
                  ))}
                </div>
              )}
              <p className="font-mono text-[10px] text-gray-600 mt-4">
                {a.article_count} {a.article_count === 1 ? "story" : "stories"} · Joined {timeAgo(a.joined)}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
